import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const MAX_POLLS = 6;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refresh } = useSite();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const polls = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (polls.current >= MAX_POLLS) { setState("timeout"); return; }
      polls.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState("paid");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setState("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [refresh]);

  return (
    <div className="min-h-screen grid place-items-center p-6 grain-bg" data-testid="payment-success-page">
      <div className="surface p-10 max-w-md text-center space-y-4">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-eucalyptus-700 mx-auto animate-spin" />
            <h1 className="text-2xl font-display font-bold text-eucalyptus-900">Confirming your payment…</h1>
            <p className="text-charcoal-soft">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
            <h1 className="text-2xl font-display font-bold text-eucalyptus-900" data-testid="payment-success-title">You're all set!</h1>
            <p className="text-charcoal-soft">Your subscription is active. Everything's unlocked again.</p>
            <Button data-testid="payment-success-continue" onClick={() => navigate("/")} className="bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white">
              Back to dashboard
            </Button>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <XCircle className="w-14 h-14 text-ochre-600 mx-auto" />
            <h1 className="text-2xl font-display font-bold text-eucalyptus-900">Still processing</h1>
            <p className="text-charcoal-soft">
              We couldn't confirm the payment yet. If you completed checkout, it may take a minute to sync — refresh the Billing page shortly.
            </p>
            <Button data-testid="payment-success-billing" onClick={() => navigate("/billing")} className="bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white">
              Go to Billing
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
